import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronDown, MessageCircle, Coffee, Zap, Briefcase, Smile, Presentation, HelpCircle, Clock, Users, Handshake, Lightbulb, ExternalLink } from 'lucide-react';
import { Footer } from '../components/Footer';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: 'easeOut'
    }
  })
};
const faqs = [{
  category: 'Hur det fungerar',
  subtitle: 'Det praktiska — utan omvägar',
  color: 'sage' as const,
  questions: [{
    q: 'Vad innebär "en dag i veckan" egentligen?',
    a: 'Precis som det låter. En dag per vecka, på plats eller remote. Ibland strategi med ledningsgruppen, ibland ett svårt samtal du skjutit upp, ibland att vi tänker tillsammans kring en prioritering. Vi anpassar fokus och format utifrån ditt behov.',
    icon: Clock
  }, {
    q: 'Hur lång tid tar ett uppdrag?',
    a: 'De flesta uppdrag löper 3–6 månader. Vi låser inte in dig — fungerar det inte, slutar vi. Hur lång tid det tar beror på var vi startar och hur långt ni vill komma. Mitt mål är alltid att bygga bort mig själv — bygga in min kompetens i ert system så att ni klarar er utan mig. När det är gjort är det dags att bryta.',
    icon: Briefcase
  }, {
    q: 'Gör du PowerPoints?',
    a: 'Jag använder slides ibland, men allt bygger på interaktion — inte föreläsning. Digitalt jobbar vi i gemensamma ytor med breakouts och faciliterade workshops. På plats blir det dialog, övningar och strukturerade samtal. Det händer att jag skriver rapporter, till exempel beslutsunderlag till styrelsen — men det är aldrig startpunkten. Det är resultatet av arbete vi gjort tillsammans.',
    icon: Presentation
  }, {
    q: 'Jobbar du med alla branscher?',
    a: 'Främst organisationer på 10–50 personer, men även större, där ledarskap, struktur och genomförande spelar roll. Särskilt mycket erfarenhet av tjänsteföretag, tech och ägarledda bolag — men utmaningarna är ofta mer lika än man tror, oavsett bransch.',
    icon: Users
  }]
}, {
  category: 'Pengarna & det praktiska',
  subtitle: 'Vad det kostar och vad du får',
  color: 'terracotta' as const,
  questions: [{
    q: 'Vad kostar det?',
    a: 'Jag är transparent med prissättning och vi pratar om det redan i första samtalet. Generellt: det kostar mindre än en felrekrytering, och betydligt mindre än ytterligare ett halvår utan riktning.',
    icon: Zap
  }, {
    q: 'Vad får jag egentligen för pengarna?',
    a: 'Rörelse. Riktning. Någon som faktiskt gör saker tillsammans med dig, inte bara rekommenderar dem. Och någon som säger vad hen ser — även när det är obekvämt. Det är inte alltid mysigt, men det är alltid ärligt.',
    icon: Lightbulb
  }, {
    q: 'Vad händer om vi behöver mer tid?',
    a: 'Då löser vi det. Men min erfarenhet är att begränsningen skapar disciplin och tempo. En dag i veckan tvingar oss att fokusera på det som faktiskt spelar roll — och det brukar räcka längre än man tror.',
    icon: Clock
  }]
}, {
  category: 'Att jobba med mig',
  subtitle: 'Vem du får — och hur det känns',
  color: 'sage' as const,
  questions: [{
    q: 'Hur är du att jobba med?',
    a: 'Lyssnande och inkännande — men jag backar inte för att spegla det jag ser. Jag jobbar utforskande: letar efter mönster, system och saker under ytan för att hitta orsaken till symptomen i verksamheten. Jag pusher er att prova saker — inte mina idéer, utan era. Det viktigaste lärandet är det ni skapar själva. Min roll är att guida och facilitera så att det händer.',
    icon: Handshake
  }, {
    q: 'Kommer du med färdiga lösningar?',
    a: 'Nej. Jag jobbar ofta med problem som är svåra att förstå och som sällan har tydliga svar. Jag har ett stort bibliotek av modeller och teorier — men aldrig för att stanna i en bok. Mer för att starta samtal, skapa nya perspektiv eller som moment i en workshop. Jag plockar fram det som passar er situation. Vissa kunder vill bli lite utbildade längs vägen — då har jag material och vana att leverera det.',
    icon: Lightbulb
  }, {
    q: 'Jobbar du ensam eller har du ett team?',
    a: null,
    icon: Users
  }, {
    q: 'Vad händer om vi inte klickar?',
    a: 'Då slutar vi. Inga hard feelings, ingen faktura för första samtalet. Kemi spelar roll — och jag tänker inte låtsas annat.',
    icon: MessageCircle
  }, {
    q: 'Kan man ta en kaffe först?',
    a: 'Absolut. Det är så de flesta samarbeten börjar. Jag bjuder — eller så tar vi det digitalt. Inga förpliktelser, inga säljpitchar. Bara ett ärligt samtal om din situation.',
    icon: Coffee
  }, {
    q: 'Vad gör du när du inte jobbar?',
    a: 'Cyklar mountainbike (helst nedför), brygger öl (som vännerna dricker upp suspekt fort) och försöker hänga med mina barn. Helt vanlig människa — vilket brukar vara en fördel i den här branschen.',
    icon: Smile
  }]
}];
// Special JSX answer for the "team" question (needs a link)
function TeamAnswer() {
  return <>
      Mitt företag driver och äger jag själv, och många leveranser gör jag på
      egen hand. Men jag jobbar också med två andra förändringskonsulter i
      nätverket{' '}
      <a href="https://be-brave.se" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:text-terracotta/80 underline underline-offset-2 decoration-terracotta/30 hover:decoration-terracotta/60 transition-colors inline-flex items-center gap-1" data-id="element-310">
        Be Brave
        <ExternalLink className="w-3 h-3" data-id="element-311" />
      </a>
      . Tillsammans har vi en ännu större bredd — framför allt inom remote- och
      distanslärande, som är en av våra specialiteter.
    </>;
}
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const toggleFAQ = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };
  return <div className="min-h-screen bg-background text-ink font-sans" data-id="element-312">
      {/* Sticky top nav */}
      <motion.div className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-sm border-b border-border/50" initial={{
      y: -60
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.4
    }} data-id="element-313">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-314">
          <Link to="/" className="flex items-center gap-2 text-muted hover:text-ink transition-colors text-sm group" data-id="element-315">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-316" />
            Tillbaka
          </Link>

          <div className="hidden md:flex items-center gap-5" data-id="element-317">
            <Link to="/om-mig" className="text-sm text-muted hover:text-ink transition-colors" data-id="element-318">
              Om Peter
            </Link>
            <Link to="/omrade-1" className="text-sm text-muted hover:text-ink transition-colors" data-id="element-319">
              Leda utvecklingen
            </Link>
            <Link to="/omrade-2" className="text-sm text-muted hover:text-ink transition-colors" data-id="element-320">
              Från ett team till tre
            </Link>
            <Link to="/omrade-3" className="text-sm text-muted hover:text-ink transition-colors" data-id="element-321">
              Hybridarbete
            </Link>
            <Link to="/faq" className="text-sm font-medium text-ink border-b border-terracotta pb-0.5" data-id="element-322">
              Vanliga frågor
            </Link>
          </div>

          <a href="mailto:peter@humble-consulting.se" className="bg-terracotta hover:bg-terracotta/90 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors" data-id="element-323">
            Boka ett samtal
          </a>
        </div>
      </motion.div>

      <main className="pt-20" data-id="element-324">
        {/* ── HERO ── */}
        <section className="py-20 md:py-28 px-6 bg-background" data-id="element-325">
          <div className="max-w-4xl mx-auto text-center" data-id="element-326">
            <motion.span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-4 block" custom={0} initial="hidden" animate="visible" variants={fadeUp} data-id="element-327">
              Vanliga frågor
            </motion.span>

            <motion.h1 className="text-4xl md:text-6xl font-light text-ink leading-[1.1] mb-6" custom={1} initial="hidden" animate="visible" variants={fadeUp} data-id="element-328">
              Saker folk undrar{' '}
              <span className="text-terracotta" data-id="element-329">— men inte alltid frågar</span>
            </motion.h1>

            <motion.p className="text-xl text-muted font-light max-w-2xl mx-auto leading-relaxed mb-8" custom={2} initial="hidden" animate="visible" variants={fadeUp} data-id="element-330">
              Här är svaren. Rakt på, med lite glimten i ögat.
            </motion.p>
          </div>
        </section>

        {/* ── FAQ LIST ── */}
        <section className="pb-20 md:pb-28 px-6" data-id="element-331">
          <div className="max-w-3xl mx-auto" data-id="element-332">
            {faqs.map((section, sectionIndex) => {
            const accentColor = section.color === 'terracotta' ? 'terracotta' : 'sage';
            return <motion.div key={sectionIndex} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: sectionIndex * 0.15
            }} className={`${sectionIndex > 0 ? 'mt-6 pt-10 border-t border-border/30' : ''}`} data-id="element-333">
                  <div className="mb-8" data-id="element-334">
                    <span className={`inline-block text-xs font-medium tracking-widest uppercase px-3 py-1.5 rounded-full ${accentColor === 'terracotta' ? 'bg-terracotta/8 text-terracotta' : 'bg-sage/10 text-sage'}`} data-id="element-335">
                      {section.category}
                    </span>
                    <p className="text-muted text-sm mt-3 ml-0.5" data-id="element-336">
                      {section.subtitle}
                    </p>
                  </div>

                  <div className="space-y-3" data-id="element-337">
                    {section.questions.map((item, i) => {
                  const id = `${sectionIndex}-${i}`;
                  const isOpen = openIndex === id;
                  // Check if this is the "team" question that needs JSX
                  const isTeamQuestion = item.q === 'Jobbar du ensam eller har du ett team?';
                  return <motion.div key={i} className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen ? `bg-card ${accentColor === 'terracotta' ? 'border-terracotta/25' : 'border-sage/25'} shadow-sm` : 'bg-background border-border/40 hover:border-border/70'}`} data-id="element-338">
                          <button onClick={() => toggleFAQ(id)} className="w-full flex items-start text-left p-5 md:p-6 gap-4 focus:outline-none" data-id="element-339">
                            <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isOpen ? `${accentColor === 'terracotta' ? 'bg-terracotta/10 text-terracotta' : 'bg-sage/10 text-sage'}` : 'text-muted/60'}`} data-id="element-340">
                              <item.icon className="w-3.5 h-3.5" strokeWidth={1.5} data-id="element-341" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-342">
                              <div className="flex items-center justify-between gap-3" data-id="element-343">
                                <h3 className={`text-base md:text-lg font-medium transition-colors leading-snug ${isOpen ? 'text-ink' : 'text-ink/80'}`} data-id="element-344">
                                  {item.q}
                                </h3>
                                <ChevronDown className={`w-4 h-4 text-muted/50 transition-transform duration-300 flex-shrink-0 ${isOpen ? `rotate-180 ${accentColor === 'terracotta' ? 'text-terracotta' : 'text-sage'}` : ''}`} data-id="element-345" />
                              </div>
                              <AnimatePresence data-id="element-346">
                                {isOpen && <motion.div initial={{
                            height: 0,
                            opacity: 0
                          }} animate={{
                            height: 'auto',
                            opacity: 1
                          }} exit={{
                            height: 0,
                            opacity: 0
                          }} transition={{
                            duration: 0.3,
                            ease: 'easeInOut'
                          }} data-id="element-347">
                                    <p className="text-muted leading-relaxed mt-3 text-sm md:text-base pr-2" data-id="element-348">
                                      {isTeamQuestion ? <TeamAnswer data-id="element-349" /> : item.a}
                                    </p>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>
                          </button>
                        </motion.div>;
                })}
                  </div>
                </motion.div>;
          })}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-20 md:py-28 px-6 bg-card/30" data-id="element-350">
          <div className="max-w-2xl mx-auto text-center" data-id="element-351">
            <motion.div initial={{
            opacity: 0,
            scale: 0.97
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-background rounded-3xl p-10 md:p-16 border border-border/50 shadow-sm" data-id="element-352">
              <div className="flex items-center justify-center gap-2 mb-6" data-id="element-353">
                <HelpCircle className="w-8 h-8 text-terracotta mb-2" strokeWidth={1.5} data-id="element-354" />
              </div>

              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-355">
                Fick du inte svar på din fråga?
              </h2>
              <p className="text-muted text-lg leading-relaxed mb-3 max-w-sm mx-auto" data-id="element-356">
                Det enklaste sättet att få veta är att fråga mig direkt.
              </p>
              <p className="text-muted text-sm italic mb-10" data-id="element-357">
                Jag bits inte. Och jag svarar snabbare än du tror.
              </p>

              <a href="mailto:peter@humble-consulting.se" className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta/90 text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-200 hover:scale-105 shadow-lg shadow-terracotta/20" data-id="element-358">
                Maila din fråga
                <ArrowRight className="w-5 h-5" data-id="element-359" />
              </a>

              <div className="mt-8" data-id="element-360">
                <Link to="/" className="text-muted hover:text-ink transition-colors text-sm flex items-center justify-center gap-2 group" data-id="element-361">
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-362" />
                  Tillbaka till startsidan
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer data-id="element-363" />
    </div>;
}